import { useEffect, useState } from 'react';
import request from 'lib/request';
import type {
  EntityDocumentsResponse,
  EntityLinkedDocumentResponse,
} from 'types/api-responses';

// Multi-entity companion to useEntityDocuments: fetches the citing documents
// for every selected entity (POST /entities/:id/queries/documents) to feed the
// preview panel's selection-wide View Documents table. Lazy (`enabled` flips on
// first disclosure open) and incremental — already-fetched entities are kept
// when the selection changes, only new ids are requested. First page per
// entity, same as the drilldown (a peek, not the full archive).
export function useEntitiesDocuments(entityIds: number[], enabled: boolean) {
  const [docsByEntity, setDocsByEntity] = useState<
    Record<number, EntityLinkedDocumentResponse[]>
  >({});
  const [loading, setLoading] = useState(false);

  // Key the effect on the id list's content, not its (per-render) identity.
  const idsKey = [...entityIds].sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    const ids = idsKey ? idsKey.split(',').map(Number) : [];
    const missing = ids.filter((id) => !(id in docsByEntity));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    setLoading(true);
    void Promise.all(
      missing.map(async (id) => {
        const response = await request.post<
          EntityDocumentsResponse,
          { error: string }
        >(
          `/entities/${id}/queries/documents`,
          { page: 1, page_size: 10 },
          new AbortController()
        );
        return [
          id,
          response.type === 'success' ? response.data.results : [],
        ] as const;
      })
    ).then((pairs) => {
      if (cancelled) return;
      setDocsByEntity((current) => ({
        ...current,
        ...Object.fromEntries(pairs),
      }));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // docsByEntity is read for `missing` but deliberately not a dependency —
    // its own update would re-run the effect to a guaranteed no-op, and rapid
    // selection changes would double-fetch during the in-flight window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, idsKey]);

  return { docsByEntity, loading };
}
